import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import * as api from '../api';
import { getCallDebtEntries, getDebtsArray } from '../utils/callDebts';

const LIVE_LISTEN_TERMINAL_STATES = new Set([
  'ended',
  'unknown',
  'rejected',
  'call_rejected',
  'ami_error',
  'ami_timeout',
  'hangup',
  'call_ended',
  'disconnect',
  'goodbye',
  'user_goodbye',
  'assistant_goodbye',
  'idle_timeout',
  'max_duration',
  'busy',
  'no_answer',
  'client_stop',
  'no_audiosocket',
  'invalid_number',
  'network_error',
  'timeout',
  'error',
  'voicemail',
  'voicemail_detected',
  'pipeline_start_failed',
  'audiosocket_handler_error',
]);
const SUPPRESSED_LISTEN_TTL_MS = 30000;

const fmtMoney = (n) => {
  const v = Number(n);
  if (!isFinite(v)) return '—';
  return new Intl.NumberFormat('bg-BG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

const toMs = (v) => {
  const raw = typeof v === 'string' ? v.trim() : v;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) {
    if (n > 1e12) return n;
    if (n > 1e10) return n;
    return n * 1000;
  }

  if (typeof raw === 'string' && raw) {
    const parsed = Date.parse(raw);
    if (Number.isFinite(parsed)) return parsed;
  }

  return null;
};

const fmtStartedAt = (v) => {
  const ms = toMs(v);
  if (!ms) return '—';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';

  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
};

const dash = (v) => (v == null || String(v).trim() === '' ? '—' : String(v).trim());

const STATUS_BG_MAP = {
  disconnect: 'Прекъснат',
  busy: 'Зает',
  error: 'Грешка',
  no_answer: 'Свободен сигнал',
  client_stop: 'Прекратено от клиента',
  no_audiosocket: 'Неуспешно свързване',
  goodbye: 'Приключил разговор',
  idle_timeout: 'Липса на активност',
  max_duration: 'Прекъснат - лимит на време',
  requested: 'Заявен',
  running: 'Активен',
  accepted: 'Активен',
  ended: 'Приключен',
  hangup: 'Затворен',
  rejected: 'Отхвърлен',
  invalid_number: 'Невалиден номер',
  network_error: 'Мрежова грешка',
  timeout: 'Изтекло време',
  voicemail: 'Гласова поща',
  voicemail_detected: 'Гласова поща',
  unknown: 'Неизвестна причина',
};

const pickStatusSourceValue = (v) => {
  if (typeof v === 'string') {
    return v.trim();
  }

  if (v && typeof v === 'object') {
    const keys = ['reason', 'code', 'type', 'value', 'status'];
    for (const key of keys) {
      const raw = v?.[key];
      if (raw == null) continue;
      const str = String(raw).trim();
      if (str) return str;
    }
  }

  if (v == null) return '';
  return String(v).trim();
};

const resolveStatusRaw = (item) => {
  const endReason = pickStatusSourceValue(item?.end_reason);
  if (endReason && endReason.toLowerCase() !== 'unknown') return endReason;

  const endReasonRaw = pickStatusSourceValue(item?.end_reason_raw ?? item?.endReasonRaw);
  if (endReasonRaw) return endReasonRaw;

  if (endReason) return endReason;

  const state = pickStatusSourceValue(item?.state);
  if (state) return state;

  return '';
};

const toBgStatusLabel = (raw) => {
  const normalized = String(raw || '').trim();
  if (!normalized) return dash(normalized);

  const lower = normalized.toLowerCase();
  if (lower === 'call_ended' || lower === 'user_goodbye' || lower === 'assistant_goodbye') {
    return STATUS_BG_MAP.goodbye || normalized;
  }
  if (lower === 'call_rejected') {
    return STATUS_BG_MAP.rejected || normalized;
  }
  if (lower === 'pipeline_start_failed' || lower === 'audiosocket_handler_error') {
    return STATUS_BG_MAP.error || normalized;
  }

  const translated = STATUS_BG_MAP[lower];
  return translated || normalized;
};

function normalizeRtcIceServers(rawServers, { fallbackStun = true } = {}) {
  const normalized = Array.isArray(rawServers)
    ? rawServers
        .map((item) => {
          if (typeof item === 'string' && item.trim()) return { urls: item.trim() };
          if (item && typeof item === 'object' && item.urls) return item;
          return null;
        })
        .filter(Boolean)
    : [];

  if (normalized.length > 0) return normalized;
  return fallbackStun ? [{ urls: 'stun:stun.l.google.com:19302' }] : [];
}

function hasTurnIceServer(iceServers) {
  return iceServers.some((server) => {
    const urls = Array.isArray(server?.urls) ? server.urls : [server?.urls];
    return urls.some((url) => {
      const value = String(url || '').trim().toLowerCase();
      return value.startsWith('turn:') || value.startsWith('turns:');
    });
  });
}

function waitForIceGatheringComplete(pc, timeoutMs = 4000) {
  if (!pc || pc.iceGatheringState === 'complete') {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let done = false;
    let timer = null;

    const finish = () => {
      if (done) return;
      done = true;
      if (timer) window.clearTimeout(timer);
      pc.removeEventListener('icegatheringstatechange', onChange);
      resolve();
    };

    const onChange = () => {
      if (pc.iceGatheringState === 'complete') finish();
    };

    timer = window.setTimeout(finish, timeoutMs);
    pc.addEventListener('icegatheringstatechange', onChange);
  });
}

export default function ConvaiHistory({
  onOpenConversation,
  callType = 'web_call',
  onCallTypeChange,
}) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [listenErr, setListenErr] = useState('');

  const [items, setItems] = useState([]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const searchQuery = debouncedQ.trim();
  const isSearching = Boolean(searchQuery);

  const [openBusyId, setOpenBusyId] = useState(null);
  const [hangupBusyId, setHangupBusyId] = useState(null);
  const [listenCallId, setListenCallId] = useState('');
  const [listenPhase, setListenPhase] = useState('idle');
  const [listenVisualCallId, setListenVisualCallId] = useState('');
  const [listenVisualRowId, setListenVisualRowId] = useState('');
  const [listenSelectedCallId, setListenSelectedCallId] = useState('');
  const [listenSelectedRowId, setListenSelectedRowId] = useState('');
  const [listenMode, setListenMode] = useState('listen');
  const [listenNeedsUserAction, setListenNeedsUserAction] = useState(false);
  const [listenLevel, setListenLevel] = useState(0);
  const [suppressedListenCalls, setSuppressedListenCalls] = useState({});
  const isListenLive = listenPhase !== 'idle';
  const activeListenCallId = listenCallId;
  const visualListenCallId = listenSelectedCallId || listenVisualCallId || activeListenCallId;
  const visualListenRowId = listenSelectedRowId || listenVisualRowId;
  const listenStatusLabel = isListenLive
    ? (listenMode === 'takeover' ? 'Агентът е в разговора' : 'Слушането е активно')
    : 'Слушай';
  const activeCallType = callType === 'phone_call' ? 'phone_call' : 'web_call';

  const aliveRef = useRef(true);
  const listenAudioRef = useRef(null);
  const listenPcRef = useRef(null);
  const listenMicStreamRef = useRef(null);
  const listenReconnectTimerRef = useRef(null);
  const listenTerminalWatchRef = useRef(null);
  const listenReconnectAttemptsRef = useRef(0);
  const listenDesiredRef = useRef({ callId: '', rowId: '', mode: 'listen' });
  const listenPageUnloadRef = useRef(false);
  const stopListeningRef = useRef(null);
  const startListeningRef = useRef(null);
  const listenMeterContextRef = useRef(null);
  const listenMeterSourceRef = useRef(null);
  const listenMeterAnalyserRef = useRef(null);
  const listenMeterDataRef = useRef(null);
  const listenMeterFrameRef = useRef(null);

  useEffect(() => () => {
    aliveRef.current = false;
  }, []);

  const clearListenReconnect = useCallback(() => {
    if (listenReconnectTimerRef.current) {
      window.clearTimeout(listenReconnectTimerRef.current);
      listenReconnectTimerRef.current = null;
    }
  }, []);

  const clearListenTerminalWatch = useCallback(() => {
    if (listenTerminalWatchRef.current) {
      window.clearTimeout(listenTerminalWatchRef.current);
      listenTerminalWatchRef.current = null;
    }
  }, []);

  const isTerminalListenError = useCallback((message) => {
    const value = String(message || '').toLowerCase();
    if (!value) return false;
    return (
      value.includes('call_not_live')
      || value.includes('state ended')
      || value.includes('state unknown')
      || value.includes('state rejected')
      || value.includes('ami_error')
      || value.includes('ami_timeout')
      || value.includes('404')
      || value.includes('403')
      || value.includes('401')
      || value.includes('missing call_id')
      || value.includes('supervisor_hub_closed')
    );
  }, []);

  const syncListenCallSnapshot = useCallback(async (callId) => {
    const targetId = String(callId || '').trim();
    if (!targetId) return { call: null, isTerminal: false, terminalRaw: '' };

    const res = await api.getCallById(targetId, { include_turns: 0 });
    const call = (res?.call && typeof res.call === 'object') ? res.call : null;

    if (call) {
      setItems((prev) => prev.map((item) => (
        String(item?.call_id || '').trim() === targetId
          ? { ...item, ...call }
          : item
      )));
    }

    const terminalRaw = resolveStatusRaw(call).toLowerCase();
    return {
      call,
      isTerminal: LIVE_LISTEN_TERMINAL_STATES.has(terminalRaw),
      terminalRaw,
    };
  }, []);

  const suppressListenCall = useCallback((callId, ttlMs = SUPPRESSED_LISTEN_TTL_MS) => {
    const targetId = String(callId || '').trim();
    if (!targetId) return;
    const expiresAt = Date.now() + Math.max(1000, Number(ttlMs) || SUPPRESSED_LISTEN_TTL_MS);
    setSuppressedListenCalls((prev) => ({ ...prev, [targetId]: expiresAt }));
  }, []);

  const clearSuppressedListenCall = useCallback((callId) => {
    const targetId = String(callId || '').trim();
    if (!targetId) return;
    setSuppressedListenCalls((prev) => {
      if (!(targetId in prev)) return prev;
      const next = { ...prev };
      delete next[targetId];
      return next;
    });
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();
      setSuppressedListenCalls((prev) => {
        const entries = Object.entries(prev).filter(([, expiresAt]) => Number(expiresAt) > now);
        if (entries.length === Object.keys(prev).length) return prev;
        return Object.fromEntries(entries);
      });
    }, 5000);

    return () => window.clearInterval(timer);
  }, []);

  const markCallLocallyEnded = useCallback((callId, endReason = 'call_ended') => {
    const targetId = String(callId || '').trim();
    if (!targetId) return;
    suppressListenCall(targetId);

    setItems((prev) => prev.map((item) => (
      String(item?.call_id || '').trim() === targetId
        ? {
            ...item,
            state: 'ended',
            end_reason: String(endReason || 'call_ended').trim() || 'call_ended',
          }
        : item
    )));
  }, [suppressListenCall]);

  const watchListenTerminalState = useCallback((callId, retries = 6) => {
    const targetId = String(callId || '').trim();
    if (!targetId || retries <= 0) return;

    clearListenTerminalWatch();
    listenTerminalWatchRef.current = window.setTimeout(async () => {
      listenTerminalWatchRef.current = null;
      if (!aliveRef.current) return;

      try {
        const { isTerminal } = await syncListenCallSnapshot(targetId);
        if (isTerminal) {
          stopListeningRef.current?.({ silent: true });
          return;
        }
      } catch {
        // Try again on the next cycle while the backend finishes persisting the call.
      }

      watchListenTerminalState(targetId, retries - 1);
    }, 1500);
  }, [clearListenTerminalWatch, syncListenCallSnapshot]);

  const scheduleListenReconnect = useCallback((callId, rowId = '') => {
    const targetId = String(callId || '').trim();
    const targetRowId = String(rowId || '').trim();
    if (!targetId) return;
    if (listenPageUnloadRef.current || !aliveRef.current) return;
    if (listenDesiredRef.current.callId !== targetId) return;
    if (listenDesiredRef.current.mode === 'takeover') return;

    clearListenReconnect();

    const attempt = Math.min(listenReconnectAttemptsRef.current + 1, 6);
    listenReconnectAttemptsRef.current = attempt;
    const delayMs = Math.min(500 * (2 ** Math.max(0, attempt - 1)), 2500);

    setListenCallId(targetId);
    setListenVisualCallId(targetId);
    setListenVisualRowId(targetRowId);
    setListenSelectedCallId(targetId);
    setListenSelectedRowId(targetRowId);
    setListenPhase('reconnecting');
    setListenNeedsUserAction(false);
    setListenErr('');

    listenReconnectTimerRef.current = window.setTimeout(() => {
      listenReconnectTimerRef.current = null;
      if (listenPageUnloadRef.current || !aliveRef.current) return;
      if (listenDesiredRef.current.callId !== targetId) return;
      void startListeningRef.current?.(targetId, targetRowId, { isReconnect: true, mode: 'listen' });
    }, delayMs);
  }, [clearListenReconnect]);

  const resumeListenAudioPlayback = useCallback(async () => {
    const audioEl = listenAudioRef.current;
    if (!audioEl) return false;

    try {
      const playPromise = audioEl.play?.();
      if (playPromise?.then) {
        await playPromise;
      }
      if (aliveRef.current) {
        setListenNeedsUserAction(false);
        setListenErr('');
      }
      return true;
    } catch {
      if (aliveRef.current) {
        setListenNeedsUserAction(true);
        setListenErr('Натисни слушалката още веднъж, за да пуснеш звука.');
      }
      return false;
    }
  }, []);

  const stopListenMeter = useCallback(() => {
    if (listenMeterFrameRef.current) {
      window.cancelAnimationFrame(listenMeterFrameRef.current);
      listenMeterFrameRef.current = null;
    }

    if (listenMeterSourceRef.current) {
      try { listenMeterSourceRef.current.disconnect(); } catch {}
      listenMeterSourceRef.current = null;
    }

    listenMeterAnalyserRef.current = null;
    listenMeterDataRef.current = null;

    const ctx = listenMeterContextRef.current;
    listenMeterContextRef.current = null;
    if (ctx) {
      try { void ctx.close(); } catch {}
    }

    setListenLevel(0);
  }, []);

  const startListenMeter = useCallback(async (stream) => {
    stopListenMeter();

    if (!stream) return;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    try {
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch {}
      }

      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.85;
      source.connect(analyser);

      const data = new Uint8Array(analyser.fftSize);

      listenMeterContextRef.current = ctx;
      listenMeterSourceRef.current = source;
      listenMeterAnalyserRef.current = analyser;
      listenMeterDataRef.current = data;

      const tick = () => {
        const activeAnalyser = listenMeterAnalyserRef.current;
        const activeData = listenMeterDataRef.current;
        if (!activeAnalyser || !activeData) return;

        activeAnalyser.getByteTimeDomainData(activeData);
        let sum = 0;
        for (let i = 0; i < activeData.length; i += 1) {
          const centered = (activeData[i] - 128) / 128;
          sum += centered * centered;
        }
        const rms = Math.sqrt(sum / activeData.length);
        const normalized = Math.max(0.04, Math.min(1, (rms - 0.008) * 5.5));
        setListenLevel((prev) => (prev * 0.45) + (normalized * 0.55));
        listenMeterFrameRef.current = window.requestAnimationFrame(tick);
      };

      tick();
    } catch {
      stopListenMeter();
    }
  }, [stopListenMeter]);

  const releaseListenPeer = useCallback(() => {
    stopListenMeter();

    const pc = listenPcRef.current;
    listenPcRef.current = null;
    if (pc) {
      try {
        pc.ontrack = null;
        pc.onconnectionstatechange = null;
        pc.getSenders?.().forEach((sender) => {
          try { sender.track?.stop?.(); } catch {}
        });
        pc.getReceivers?.().forEach((receiver) => {
          try { receiver.track?.stop?.(); } catch {}
        });
        pc.close();
      } catch {}
    }

    const micStream = listenMicStreamRef.current;
    listenMicStreamRef.current = null;
    if (micStream) {
      try {
        micStream.getTracks().forEach((track) => track.stop());
      } catch {}
    }

    if (listenAudioRef.current) {
      try {
        listenAudioRef.current.pause();
        listenAudioRef.current.srcObject = null;
      } catch {}
    }
  }, [stopListenMeter]);

  const stopListening = useCallback(({ silent = false } = {}) => {
    listenDesiredRef.current = { callId: '', rowId: '', mode: 'listen' };
    listenReconnectAttemptsRef.current = 0;
    clearListenReconnect();
    clearListenTerminalWatch();
    if (aliveRef.current) {
      flushSync(() => {
        setListenPhase('idle');
        setListenCallId('');
        setListenVisualCallId('');
        setListenVisualRowId('');
        setListenSelectedCallId('');
        setListenSelectedRowId('');
        setListenMode('listen');
        setListenNeedsUserAction(false);
        if (silent) {
          setListenErr('');
        }
      });
    }
    releaseListenPeer();
  }, [clearListenReconnect, clearListenTerminalWatch, releaseListenPeer]);

  useEffect(() => {
    stopListeningRef.current = stopListening;
  }, [stopListening]);

  useEffect(() => {
    const handlePageHide = () => {
      listenPageUnloadRef.current = true;
      listenDesiredRef.current = { callId: '', rowId: '', mode: 'listen' };
      listenReconnectAttemptsRef.current = 0;
      clearListenReconnect();
      clearListenTerminalWatch();
      releaseListenPeer();
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
    };
  }, [clearListenReconnect, clearListenTerminalWatch, releaseListenPeer]);

  useEffect(() => {
    const audioEl = listenAudioRef.current;

    return () => {
      stopListenMeter();
      clearListenReconnect();
      clearListenTerminalWatch();

      const pc = listenPcRef.current;
      listenPcRef.current = null;
      if (pc) {
        try {
          pc.ontrack = null;
          pc.onconnectionstatechange = null;
          pc.getSenders?.().forEach((sender) => {
            try { sender.track?.stop?.(); } catch {}
          });
          pc.getReceivers?.().forEach((receiver) => {
            try { receiver.track?.stop?.(); } catch {}
          });
          pc.close();
        } catch {}
      }

      const micStream = listenMicStreamRef.current;
      listenMicStreamRef.current = null;
      if (micStream) {
        try {
          micStream.getTracks().forEach((track) => track.stop());
        } catch {}
      }

      if (audioEl) {
        try {
          audioEl.pause();
          audioEl.srcObject = null;
        } catch {}
      }
    };
  }, [clearListenReconnect, clearListenTerminalWatch, stopListenMeter]);

  useEffect(() => {
    if (activeCallType === 'phone_call' || listenPhase === 'idle') {
      return;
    }

    releaseListenPeer();
    if (!aliveRef.current) return;
    setListenPhase('idle');
    setListenCallId('');
    setListenVisualCallId('');
    setListenVisualRowId('');
    setListenSelectedCallId('');
    setListenSelectedRowId('');
    setListenMode('listen');
    setListenNeedsUserAction(false);
    setListenErr('');
  }, [activeCallType, listenPhase, releaseListenPeer]);

  const handleOpen = async (callId) => {
    if (!callId) return;
    if (openBusyId) return;

    setOpenBusyId(callId);
    setErr('');
    try {
      const ret = onOpenConversation?.(callId);
      if (ret && typeof ret.then === 'function') {
        await ret;
      }
    } catch (e) {
      if (aliveRef.current) setErr(e?.message || 'Грешка при отваряне.');
    } finally {
      if (aliveRef.current) setOpenBusyId(null);
    }
  };

  const startListening = useCallback(async (callId, rowId = '', { isReconnect = false, mode = 'listen' } = {}) => {
    const targetId = String(callId || '').trim();
    const targetRowId = String(rowId || '').trim();
    const targetMode = mode === 'takeover' ? 'takeover' : 'listen';
    const isTakeover = targetMode === 'takeover';
    if (!targetId) return;
    let connectedOnce = false;
    let terminalHandled = false;
    let iceDisconnectTimer = null;
    let sawRemoteTrack = false;

    clearSuppressedListenCall(targetId);
    listenPageUnloadRef.current = false;
    listenDesiredRef.current = { callId: targetId, rowId: targetRowId, mode: targetMode };
    clearListenReconnect();
    releaseListenPeer();
    if (aliveRef.current) {
      flushSync(() => {
        setListenErr('');
        setListenCallId(targetId);
        setListenVisualCallId(targetId);
        setListenVisualRowId(targetRowId);
        setListenSelectedCallId(targetId);
        setListenSelectedRowId(targetRowId);
        setListenMode(targetMode);
        setListenPhase(isReconnect ? 'reconnecting' : 'connecting');
        setListenNeedsUserAction(false);
      });
    }
    if (!isReconnect) {
      listenReconnectAttemptsRef.current = 0;
    }

    try {
      const session = await api.createCallSupervisorSession(targetId, { mode: targetMode });
      const iceServers = normalizeRtcIceServers(session?.ice_servers, { fallbackStun: false });
      const iceGatherTimeoutMs = hasTurnIceServer(iceServers) ? 4000 : 700;
      const pc = new RTCPeerConnection({
        iceServers,
      });
      listenPcRef.current = pc;

      const clearIceDisconnectTimer = () => {
        if (iceDisconnectTimer) {
          window.clearTimeout(iceDisconnectTimer);
          iceDisconnectTimer = null;
        }
      };

      const handlePeerEnded = (source, stateValue, { forceTerminalUi = false } = {}) => {
        if (terminalHandled) return;
        terminalHandled = true;
        clearIceDisconnectTimer();
        releaseListenPeer();
        if (listenPageUnloadRef.current || listenDesiredRef.current.callId !== targetId) {
          setListenPhase('idle');
          setListenCallId('');
          setListenVisualCallId('');
          setListenVisualRowId('');
          setListenNeedsUserAction(false);
          setListenErr('Live audio disconnected.');
          return;
        }
        if (isTakeover) {
          listenDesiredRef.current = { callId: '', rowId: '', mode: 'listen' };
          setListenPhase('idle');
          setListenCallId('');
          setListenVisualCallId('');
          setListenVisualRowId('');
          setListenSelectedCallId('');
          setListenSelectedRowId('');
          setListenMode('listen');
          setListenNeedsUserAction(false);
          setListenErr('Agent audio disconnected. The bot remains stopped for this call.');
          return;
        }
        if (forceTerminalUi) {
          markCallLocallyEnded(targetId, 'call_ended');
          stopListening({ silent: true });
          watchListenTerminalState(targetId);
          return;
        }
        void (async () => {
          try {
            const { isTerminal } = await syncListenCallSnapshot(targetId);
            if (isTerminal) {
              stopListening({ silent: true });
              return;
            }
          } catch {
          }

          if (!aliveRef.current) return;
          if (listenPageUnloadRef.current || listenDesiredRef.current.callId !== targetId) return;
          if (connectedOnce) {
            markCallLocallyEnded(targetId, 'call_ended');
            stopListening({ silent: true });
            watchListenTerminalState(targetId);
            return;
          }
          scheduleListenReconnect(targetId, targetRowId);
        })();
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
          clearIceDisconnectTimer();
          return;
        }
        if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'closed') {
          handlePeerEnded('iceconnectionstatechange', pc.iceConnectionState);
          return;
        }
        if (pc.iceConnectionState === 'disconnected') {
          const shouldForceTerminalUi =
            sawRemoteTrack ||
            connectedOnce ||
            pc.connectionState === 'connected' ||
            pc.connectionState === 'disconnected';
          if (shouldForceTerminalUi) {
            handlePeerEnded('iceconnectionstatechange', pc.iceConnectionState, { forceTerminalUi: true });
            return;
          }
          if (connectedOnce) {
            handlePeerEnded('iceconnectionstatechange', pc.iceConnectionState, { forceTerminalUi: true });
            return;
          }
          clearIceDisconnectTimer();
          iceDisconnectTimer = window.setTimeout(() => {
            iceDisconnectTimer = null;
            if (!aliveRef.current || listenPcRef.current !== pc) return;
            if (pc.iceConnectionState === 'disconnected') {
              handlePeerEnded('iceconnectionstatechange', pc.iceConnectionState);
            }
          }, 1200);
          return;
        }
        clearIceDisconnectTimer();
      };

      if (isTakeover) {
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        listenMicStreamRef.current = micStream;
        micStream.getAudioTracks().forEach((track) => pc.addTrack(track, micStream));
      } else {
        pc.addTransceiver('audio', { direction: 'recvonly' });
      }
      pc.ontrack = (event) => {
        const stream = event?.streams?.[0] || new MediaStream([event.track]);
        sawRemoteTrack = true;
        if (event?.track) {
          event.track.onended = () => {
            if (!aliveRef.current) return;
            if (listenPageUnloadRef.current || listenDesiredRef.current.callId !== targetId) return;
            markCallLocallyEnded(targetId, 'call_ended');
            stopListening({ silent: true });
            watchListenTerminalState(targetId);
          };
        }
        if (aliveRef.current) {
          connectedOnce = true;
          listenReconnectAttemptsRef.current = 0;
          setListenCallId(targetId);
          setListenPhase('connected');
        }
        void startListenMeter(stream);
        if (listenAudioRef.current) {
          listenAudioRef.current.srcObject = stream;
          void resumeListenAudioPlayback();
        }
      };
      pc.onconnectionstatechange = () => {
        if (!aliveRef.current || listenPcRef.current !== pc) return;
        const nextState = pc.connectionState;
        if (nextState === 'connected') {
          connectedOnce = true;
          listenReconnectAttemptsRef.current = 0;
          clearListenReconnect();
          setListenCallId(targetId);
          setListenPhase('connected');
          setListenNeedsUserAction(false);
          return;
        }
        if (nextState === 'connecting') {
          setListenCallId(targetId);
          setListenPhase('connecting');
          return;
        }
        if (nextState === 'failed' || nextState === 'disconnected' || nextState === 'closed') {
          handlePeerEnded('connectionstatechange', nextState);
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitForIceGatheringComplete(pc, iceGatherTimeoutMs);

      const res = await api.createCallSupervisorOffer(targetId, {
        sdp: pc.localDescription?.sdp || offer.sdp,
        type: pc.localDescription?.type || offer.type,
        mode: targetMode,
      });
      const answer = res?.answer;
      if (!answer?.sdp || !answer?.type) {
        throw new Error('Supervisor answer is missing SDP.');
      }

      await pc.setRemoteDescription({ type: answer.type, sdp: answer.sdp });
    } catch (e) {
      const errorMessage = e?.message || 'Live audio start failed.';
      releaseListenPeer();
      if (aliveRef.current) {
        const isTerminal = isTerminalListenError(errorMessage);
        if (!isTakeover && !listenPageUnloadRef.current && listenDesiredRef.current.callId === targetId && !isTerminal) {
          scheduleListenReconnect(targetId, targetRowId);
          return;
        }
        if (isTerminal) {
          try {
            await syncListenCallSnapshot(targetId);
          } catch {}
          stopListening({ silent: true });
          watchListenTerminalState(targetId);
          return;
        }
        listenDesiredRef.current = { callId: '', rowId: '', mode: 'listen' };
        listenReconnectAttemptsRef.current = 0;
        clearListenReconnect();
        setListenPhase('idle');
        setListenCallId('');
        setListenVisualCallId('');
        setListenVisualRowId('');
        setListenSelectedCallId('');
        setListenSelectedRowId('');
        setListenMode('listen');
        setListenNeedsUserAction(false);
        setListenErr(errorMessage);
      }
    }
  }, [
    clearListenReconnect,
    clearSuppressedListenCall,
    stopListening,
    isTerminalListenError,
    markCallLocallyEnded,
    releaseListenPeer,
    resumeListenAudioPlayback,
    scheduleListenReconnect,
    syncListenCallSnapshot,
    startListenMeter,
    watchListenTerminalState,
  ]);

  useEffect(() => {
    startListeningRef.current = startListening;
  }, [startListening]);

  const handleListenToggle = async (callId, rowId = '') => {
    const targetId = String(callId || '').trim();
    const targetRowId = String(rowId || '').trim();
    if (!targetId) return;

    const activeVisualCallId = listenSelectedCallId || listenVisualCallId || listenCallId;
    const activeVisualRowId = listenSelectedRowId || listenVisualRowId;
    const isSameTarget =
      (Boolean(targetRowId) && targetRowId === activeVisualRowId)
      || targetId === activeVisualCallId;

    if (
      isSameTarget &&
      (
        listenPhase !== 'idle'
        || Boolean(listenPcRef.current)
        || listenNeedsUserAction
        || listenDesiredRef.current.callId === targetId
      )
    ) {
      if (listenNeedsUserAction) {
        await resumeListenAudioPlayback();
      } else {
        stopListening({ silent: true });
      }
      return;
    }

    setListenSelectedCallId(targetId);
    setListenSelectedRowId(targetRowId);
    await startListening(targetId, rowId, { mode: 'listen' });
  };

  const handleTakeoverToggle = async (callId, rowId = '') => {
    const targetId = String(callId || '').trim();
    const targetRowId = String(rowId || '').trim();
    if (!targetId) return;

    const activeVisualCallId = listenSelectedCallId || listenVisualCallId || listenCallId;
    const activeVisualRowId = listenSelectedRowId || listenVisualRowId;
    const isSameTarget =
      (Boolean(targetRowId) && targetRowId === activeVisualRowId)
      || targetId === activeVisualCallId;

    if (
      isSameTarget
      && listenMode === 'takeover'
      && (
        listenPhase !== 'idle'
        || Boolean(listenPcRef.current)
        || listenDesiredRef.current.callId === targetId
      )
    ) {
      stopListening({ silent: true });
      return;
    }

    setListenSelectedCallId(targetId);
    setListenSelectedRowId(targetRowId);
    await startListening(targetId, rowId, { mode: 'takeover' });
  };

  const refreshCallSnapshotSoon = useCallback((callId, delayMs = 1500) => {
    const targetId = String(callId || '').trim();
    if (!targetId) return;

    window.setTimeout(() => {
      if (!aliveRef.current) return;
      void syncListenCallSnapshot(targetId).catch(() => {});
    }, Math.max(0, Number(delayMs) || 0));
  }, [syncListenCallSnapshot]);

  const handleHangup = useCallback(async (callId) => {
    const targetId = String(callId || '').trim();
    if (!targetId || hangupBusyId === targetId) return;

    setErr('');
    setHangupBusyId(targetId);

    try {
      await api.hangupCall(targetId);
      markCallLocallyEnded(targetId, 'hangup');

      if (
        activeListenCallId === targetId
        || visualListenCallId === targetId
        || listenDesiredRef.current.callId === targetId
      ) {
        stopListening({ silent: true });
      }

      refreshCallSnapshotSoon(targetId);
    } catch (e) {
      if (aliveRef.current) {
        setErr(e?.message || 'Грешка при прекъсване на разговора.');
      }
    } finally {
      if (aliveRef.current) {
        setHangupBusyId((current) => (current === targetId ? null : current));
      }
    }
  }, [
    activeListenCallId,
    hangupBusyId,
    markCallLocallyEnded,
    refreshCallSnapshotSoon,
    stopListening,
    visualListenCallId,
  ]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q), 350);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => { setPage(1); }, [debouncedQ, perPage, activeCallType]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setErr('');
      try {
        if (isSearching) {
          const pageSize = 200;
          let p = 1;
          let pages = 1;
          const all = [];

          while (p <= pages) {
            const res = await api.listCalls({
              page: p,
              per_page: pageSize,
              q: null,
              type: activeCallType,
            });

            if (cancelled) return;

            const chunk = Array.isArray(res?.items) ? res.items : [];
            all.push(...chunk);
            pages = Math.max(1, Number(res?.total_pages || 1));
            p += 1;
            }

            if (cancelled) return;
            setItems(all);
            setTotalPages(1);
          } else {
            const res = await api.listCalls({
            page,
            per_page: perPage,
            q: null,
            type: activeCallType,
            });

            if (cancelled) return;

            setItems(Array.isArray(res?.items) ? res.items : []);
            setTotalPages(Math.max(1, Number(res?.total_pages || 1)));
          }
        } catch (e) {
          if (cancelled) return;
          setErr(e?.message || 'Грешка при зареждане.');
          setItems([]);
          setTotalPages(1);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();

    return () => { cancelled = true; };
  }, [page, perPage, activeCallType, isSearching]);

  useEffect(() => {
    if (activeCallType !== 'phone_call') return undefined;

    let cancelled = false;
    const timer = window.setInterval(() => {
      void (async () => {
        try {
          if (isSearching) {
            const pageSize = 200;
            let p = 1;
            let pages = 1;
            const all = [];

            while (p <= pages) {
              const res = await api.listCalls({
                page: p,
                per_page: pageSize,
                q: null,
                type: activeCallType,
              });

              if (cancelled || !aliveRef.current) return;

              const chunk = Array.isArray(res?.items) ? res.items : [];
              all.push(...chunk);
              pages = Math.max(1, Number(res?.total_pages || 1));
              p += 1;
            }

            if (cancelled || !aliveRef.current) return;
            setItems(all);
            setTotalPages(1);
            return;
          }

          const res = await api.listCalls({
            page,
            per_page: perPage,
            q: null,
            type: activeCallType,
          });

          if (cancelled || !aliveRef.current) return;
          setItems(Array.isArray(res?.items) ? res.items : []);
          setTotalPages(Math.max(1, Number(res?.total_pages || 1)));
        } catch {
          // Background refresh should not disrupt the current screen state.
        }
      })();
    }, 4000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [page, perPage, activeCallType, isSearching]);

  const rows = useMemo(() => {
    const mapped = (items || []).map((it, idx) => {
      const snapshot = (it?.client_snapshot && typeof it.client_snapshot === 'object')
        ? it.client_snapshot
        : {};
      const debtSource =
        (it?.debt && typeof it.debt === 'object')
          ? { ...snapshot, debt: it.debt }
          : snapshot;

      const p = (snapshot?.personal && typeof snapshot.personal === 'object')
        ? snapshot.personal
        : ((it?.personal && typeof it.personal === 'object') ? it.personal : {});

      const debts = getDebtsArray(debtSource);
      const callDebtEntries = getCallDebtEntries(debtSource, debts);
      const d = callDebtEntries[0]?.debt || debts[0] || {};

      const conversationId = String(it?.call_id || '').trim();
      const statusRaw = resolveStatusRaw(it);
      const stateRaw = pickStatusSourceValue(it?.state).toLowerCase();
      const listenStateRaw = String(statusRaw || stateRaw || '').trim().toLowerCase();
      const normalizedType = String(it?.type || '').trim().toLowerCase();
      const suppressedUntil = Number(suppressedListenCalls[conversationId] || 0);
      const isListenSuppressed = suppressedUntil > Date.now();

      return {
        rowId: String(it?._id || it?.call_id || it?.id || it?.conversation_id || `row-${idx}`).trim(),
        conversationId,
        startedAtLabel: fmtStartedAt(
          it?.started_at ??
          it?.startedAt ??
          it?.start_time ??
          it?.created_at ??
          it?.started_at_unix_secs ??
          it?.start_time_unix_secs
        ),
        firstName: p?.firstName || '—',
        lastName: p?.lastName || '—',
        egn: p?.egn || '—',
        creditor: d?.creditor || '—',
        extraCallDebts: Math.max(0, callDebtEntries.length - 1),
        statusLabel: toBgStatusLabel(statusRaw),
        statusRaw,
        callType: normalizedType,
        stateRaw,
        listenStateRaw,
        isListenSuppressed,
        canListen:
          normalizedType === 'phone_call' &&
          Boolean(conversationId) &&
          !LIVE_LISTEN_TERMINAL_STATES.has(listenStateRaw) &&
          !isListenSuppressed,
        amount: d?.amount,
      };
    });

    if (!isSearching) return mapped;

    const qLower = searchQuery.toLowerCase();
    const qDigits = searchQuery.replace(/\D+/g, '');

    return mapped.filter((r) => {
      const first = String(r.firstName || '').toLowerCase();
      const last = String(r.lastName || '').toLowerCase();
      const egnRaw = String(r.egn || '');
      const egnLower = egnRaw.toLowerCase();
      const egnDigits = egnRaw.replace(/\D+/g, '');

      if (first.includes(qLower)) return true;
      if (last.includes(qLower)) return true;
      if (egnLower.includes(qLower)) return true;
      if (qDigits && egnDigits.includes(qDigits)) return true;
      return false;
    });
  }, [items, isSearching, searchQuery, suppressedListenCalls]);

  const canPrev = !isSearching && page > 1;
  const canNext = !isSearching && page < totalPages;
  const currentListenRow = useMemo(
    () => rows.find((row) => row.conversationId === activeListenCallId) || null,
    [rows, activeListenCallId],
  );

  useEffect(() => {
    if (!activeListenCallId) return;
    if (listenPhase === 'idle' && !listenDesiredRef.current.callId && !listenPcRef.current) return;

    const rowTerminal = currentListenRow && !currentListenRow.canListen;

    if (rowTerminal) {
      stopListening({ silent: true });
    }
  }, [activeListenCallId, currentListenRow, listenPhase, stopListening]);

  return (
    <div className="card rounded-2xl p-6">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-lg font-bold whitespace-nowrap">История на обаждания</h2>

        <div
          className="inline-flex items-center rounded-xl border border-[#1f2638] bg-[#121825] p-1"
          role="tablist"
          aria-label="Call type"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeCallType === 'web_call'}
            onClick={() => onCallTypeChange?.('web_call')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${activeCallType === 'web_call'
                ? 'bg-violet-600/30 text-violet-100 border border-violet-500/40'
                : 'text-slate-300 hover:text-slate-100'
              }`}
          >
            Уеб Обаждания
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeCallType === 'phone_call'}
            onClick={() => onCallTypeChange?.('phone_call')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(34_85_55)] ${
              activeCallType === 'phone_call'
                ? 'bg-[rgb(21_47_31)] text-green-100 border border-[rgb(15_35_24)] hover:bg-[rgb(26_58_38)]'
                : 'text-slate-300 hover:text-slate-100'
            }`}
          >
            Телефонни Обаждания
          </button>
        </div>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Търси"
            className="shrink-0 w-64 sm:w-80 md:w-[420px] bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
          />

          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            className="bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
            title="per_page"
          >
            <option value={20}>20 / стр</option>
            <option value={50}>50 / стр</option>
            <option value={100}>100 / стр</option>
          </select>
        </div>
      </div>

      {err && (
        <div className="mt-4 rounded-xl border border-rose-600/30 bg-rose-600/10 px-3 py-2 text-sm text-rose-200">
          {err}
        </div>
      )}

      {listenErr && (
        <div className="mt-4 rounded-xl border border-amber-600/30 bg-amber-600/10 px-3 py-2 text-sm text-amber-100">
          {listenErr}
        </div>
      )}

      <div className="mt-4 overflow-x-auto max-w-full">
        <table className="w-full table">
          <thead>
            <tr className="text-left">
              <th className="text-left">Дата</th>
              <th className="text-left">СТАТУС</th>
              <th className="text-left">Име</th>
              <th className="text-left">Фамилия</th>
              <th className="text-left">ЕГН</th>
              <th className="text-left">Кредитор</th>
              <th className="text-right">Сума</th>
              <th className="text-right">Действия</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((r) => {
              const isOpening = openBusyId === r.conversationId;
              const isHangingUp = hangupBusyId === r.conversationId;
              const hasListenActivity =
                listenPhase !== 'idle'
                || listenNeedsUserAction
                || Boolean(listenPcRef.current)
                || listenDesiredRef.current.callId === r.conversationId;
              const isListeningRow =
                hasListenActivity && (
                  (Boolean(r.rowId) && r.rowId === visualListenRowId) ||
                  (Boolean(r.conversationId) && r.conversationId === visualListenCallId)
                );
              const isTakeoverRow = isListeningRow && listenMode === 'takeover';
              const showListenAction = !r.isListenSuppressed && (r.canListen || isListeningRow);
              const showHangupAction = r.canListen;

              return (
                <tr key={r.rowId}>
                  <td className="font-medium text-left tabular-nums">{r.startedAtLabel}</td>
                  <td className="text-left text-slate-200">{r.statusLabel}</td>
                  <td className="font-medium text-left">{r.firstName}</td>
                  <td className="font-medium text-left">{r.lastName}</td>
                  <td className="font-medium text-left">{r.egn}</td>
                  <td className="text-left text-slate-200">
                    <div className="inline-flex items-center gap-2">
                      <span>{r.creditor}</span>
                      {r.extraCallDebts > 0 ? (
                        <span className="rounded border border-[#2a3347] bg-[#121825] px-1.5 py-0.5 text-[10px] text-slate-300 whitespace-nowrap">
                          +{r.extraCallDebts} още
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="text-right text-slate-200 tabular-nums">{fmtMoney(r.amount)}</td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      {showListenAction ? (
                        <button
                          type="button"
                          className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-all duration-150 ${
                            isListeningRow
                              ? 'border-emerald-300 text-slate-950 shadow-[0_12px_28px_rgba(52,211,153,0.32)]'
                              : 'border-[#2a3347] bg-[#121825] text-slate-200 hover:border-emerald-500/30 hover:text-emerald-100'
                          } disabled:cursor-not-allowed disabled:opacity-60`}
                          style={isListeningRow ? { backgroundColor: '#34d399' } : undefined}
                          onClick={() => { void handleListenToggle(r.conversationId, r.rowId); }}
                          disabled={isHangingUp}
                          title={isListeningRow ? 'Слушането е активно' : 'Слушай разговора'}
                          aria-label={isListeningRow ? 'Слушането е активно' : 'Слушай разговора'}
                          aria-pressed={isListeningRow}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M4 14a8 8 0 1 1 16 0" />
                            <path d="M4 14v3a2 2 0 0 0 2 2h1v-7H6a2 2 0 0 0-2 2Z" />
                            <path d="M20 14v3a2 2 0 0 1-2 2h-1v-7h1a2 2 0 0 1 2 2Z" />
                          </svg>
                        </button>
                      ) : null}
                      {showListenAction ? (
                        <button
                          type="button"
                          className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-all duration-150 ${
                            isTakeoverRow
                              ? 'border-sky-300 bg-sky-300 text-slate-950 shadow-[0_12px_28px_rgba(125,211,252,0.28)]'
                              : 'border-[#2a3347] bg-[#121825] text-slate-200 hover:border-sky-500/40 hover:text-sky-100'
                          } disabled:cursor-not-allowed disabled:opacity-60`}
                          onClick={() => { void handleTakeoverToggle(r.conversationId, r.rowId); }}
                          disabled={isHangingUp}
                          title={isTakeoverRow ? 'Agent is in the call' : 'Join and stop the bot'}
                          aria-label={isTakeoverRow ? 'Agent is in the call' : 'Join and stop the bot'}
                          aria-pressed={isTakeoverRow}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M12 18.5v3" />
                            <path d="M8 21.5h8" />
                            <path d="M7 9v2a5 5 0 0 0 10 0V9" />
                            <rect x="9" y="2.5" width="6" height="11" rx="3" />
                          </svg>
                        </button>
                      ) : null}
                      {showHangupAction ? (
                        <button
                          type="button"
                          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-rose-500/30 bg-rose-500/10 text-rose-200 transition-all duration-150 hover:border-rose-400/50 hover:bg-rose-500/15 hover:text-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => { void handleHangup(r.conversationId); }}
                          disabled={isHangingUp}
                          title="Прекъсни разговора"
                          aria-label="Прекъсни разговора"
                        >
                          {isHangingUp ? (
                            <span
                              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-80"
                              aria-hidden="true"
                            />
                          ) : (
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M21 16.2v2a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1A19.4 19.4 0 0 1 4.9 11.8 19.8 19.8 0 0 1 1.8 3.1 2 2 0 0 1 3.8.9h2a2 2 0 0 1 2 1.7l.7 4a2 2 0 0 1-.6 1.8L6.5 9.9a16 16 0 0 0 7.6 7.6l1.5-1.4a2 2 0 0 1 1.8-.6l4 .7a2 2 0 0 1 1.6 2Z" />
                              <path d="M3 3l18 18" />
                            </svg>
                          )}
                        </button>
                      ) : null}
                      <button
                        className="btn btn-ghost inline-flex items-center gap-2"
                        onClick={() => handleOpen(r.conversationId)}
                        disabled={!r.conversationId || !!openBusyId}
                        title={r.conversationId ? 'Отвори детайли' : 'Няма call_id'}
                      >
                        {isOpening && (
                          <span
                            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-80"
                            aria-hidden="true"
                          />
                        )}
                        {isOpening ? 'Отваряне…' : 'Отвори'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={8} className="text-center py-10 text-slate-400">
                  {loading ? 'Зареждане…' : 'Няма резултати.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button className="btn btn-ghost" disabled={!canPrev || loading} onClick={() => setPage((p) => p - 1)}>
          Назад
        </button>
        <div className="text-sm text-slate-400">
          Стр. {page} / {totalPages}
        </div>
        <button className="btn btn-ghost" disabled={!canNext || loading} onClick={() => setPage((p) => p + 1)}>
          Напред
        </button>
      </div>

      {false && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4">
          <div className="w-full max-w-lg rounded-2xl border border-[#26314a] bg-[#0f1524] p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Live Listen</div>
                <h3 className="mt-2 text-lg font-semibold text-slate-100">Слушане на разговор</h3>
                {currentListenRow ? (
                  <div className="mt-1 text-sm text-slate-400">
                    {`${currentListenRow.firstName} ${currentListenRow.lastName}`.trim()}
                    {currentListenRow.creditor ? ` • ${currentListenRow.creditor}` : ''}
                  </div>
                ) : null}
              </div>

              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#2a3347] text-slate-300 transition-colors hover:border-rose-500/40 hover:text-rose-200"
                onClick={() => stopListening({ silent: true })}
                title="Затвори слушането"
                aria-label="Затвори слушането"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-400" aria-hidden="true" />
                <span className="text-sm font-medium text-emerald-100">{listenStatusLabel}</span>
              </div>

              <div className="mt-4">
                <div className="mb-2 text-[11px] uppercase tracking-[0.16em] text-slate-400">Сигнал</div>
                <div className="h-3 overflow-hidden rounded-full bg-[#182033]">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-lime-300 to-emerald-200 transition-[width] duration-150"
                    style={{ width: `${Math.max(6, Math.round(listenLevel * 100))}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 text-sm text-slate-400">
              Звукът от разговора ще се възпроизвежда тук в реално време. Прозорецът се затваря автоматично, когато разговорът приключи.
            </div>
          </div>
        </div>
      )}
      <audio ref={listenAudioRef} autoPlay playsInline className="hidden" />
    </div>
  );
}
